import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  TextField,
  Alert,
  Divider
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface VersionUploadModalProps {
  open: boolean;
  onClose: () => void;
  assetId: string;
  initialFile?: File | null;
  onUploadSuccess?: () => void;
}

const VersionUploadModal: React.FC<VersionUploadModalProps> = ({ 
  open, 
  onClose, 
  assetId,
  initialFile,
  onUploadSuccess 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [note, setNote] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setUploading(false);
      setMessage('');
      setNote('');
      setIsDragOver(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  // Set initial file when provided
  useEffect(() => {
    if (open && initialFile) {
      setSelectedFile(initialFile);
      setMessage('');
    }
  }, [open, initialFile]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setMessage('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('asset', selectedFile);  // Backend expects 'asset' not 'file'
    if (note.trim()) {
      formData.append('note', note.trim());
    }

    try {
      const response = await axiosInstance.post(
        `/api/assets/${assetId}/versions`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setMessage('Version uploaded successfully!');

      const updatedAsset = response.data;
      const updatedAssetId = String(updatedAsset?._id || assetId);
      const updatedCategoryIds = Array.isArray(updatedAsset?.categories)
        ? updatedAsset.categories
            .map((categoryId: any) => String(categoryId?._id || categoryId || ''))
            .filter(Boolean)
        : [];
      window.setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
            detail: {
              assetIds: updatedAssetId ? [updatedAssetId] : [],
              categoryIds: updatedCategoryIds,
            },
          }));
        } catch {}
      }, 100);
      
      // Reset form
      setSelectedFile(null);
      setNote('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }, 1500);
      
    } catch (error: any) {
      console.error('Version upload error:', error);
      
      // Better error message parsing
      let errorMessage = 'Upload failed';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error - please check that the file is valid and try again';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={!uploading ? onClose : undefined}
      aria-labelledby="version-upload-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <Typography id="version-upload-modal-title" variant="h6" component="h2" gutterBottom>
          Ladda upp ny version
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        {/* Drag & Drop Area */}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: isDragOver ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
            transition: 'all 0.2s',
            cursor: 'pointer',
            mb: 3
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
            disabled={uploading}
          />
          
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          
          {selectedFile ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                Vald fil: <strong>{selectedFile.name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Storlek: {Math.round(selectedFile.size / 1024)} KB
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Dra och släpp fil här
              </Typography>
              <Typography variant="body2" color="textSecondary">
                eller klicka för att välja fil
              </Typography>
            </Box>
          )}
        </Box>

        {/* Version Note */}
        <TextField
          label="Versionskommentar (valfritt)"
          fullWidth
          multiline
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={uploading}
          placeholder="Beskriv vad som har ändrats i denna version..."
          sx={{ mb: 3 }}
        />

        {/* Message Display */}
        {message && (
          <Alert 
            severity={message.includes('failed') ? 'error' : 'success'} 
            sx={{ mb: 3 }}
          >
            {message}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={uploading}
          >
            Avbryt
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          >
            {uploading ? 'Laddar upp...' : 'Ladda upp version'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default VersionUploadModal; 